// TopBanner.jsx — Bandeau global en haut de toutes les pages
// Position: sticky, z-index élevé, visible sur tout le site
// IMMERSIVE: Disparaît au scroll, réapparaît uniquement en haut de page
// Note: Masqué en mode Réseau (Abracadaworld) pour laisser place au ReseauHeader
// MOBILE: Remplacé par la navbar unifiée du cockpit

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';
import { Bell, MessageCircle, User, ShoppingCart, ChevronDown, ChevronRight } from 'lucide-react';
import { useBuyer } from '../../contexts/BuyerContext';
import { useNavMode, NAV_MODES } from '../../context/NavModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import UniversHamburgerMenu from '../layout/UniversHamburgerMenu';
import { useCurrentUser } from '../../hooks/useCurrentUser';

export default function TopBanner() {
  const { shouldShowBanner, isHidden } = useImmersiveScrollContext();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Utilisateur connecté
  const { user, isAuthenticated } = useCurrentUser();
  
  // Panier
  const { cartCount } = useBuyer();
  
  // Mode Selector
  const { mode, setMode, isShopMode, isReseauMode, NAV_MODES } = useNavMode();
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  
  // État pour le sidebar mobile
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  // Détecter si on est en mode Réseau par URL (pour masquer le banner)
  const isOnReseauPage = location.pathname.startsWith('/reseau');

  // Détecter si on est dans le Cockpit - AFFICHER le header mobile dans le cockpit
  const isCockpitMode = location.pathname.startsWith('/cockpit-pro') || location.pathname.startsWith('/cockpit-particulier');

  // Détecter le mode Pro (Cockpit Pro)
  const isProMode = location.pathname.startsWith('/cockpit-pro');
  
  // Sur desktop, masquer le bandeau dans le cockpit (il a son propre layout)
  // Sur mobile, AFFICHER le TopBanner dans le cockpit pour navigation globale
  const hideBannerDesktopInCockpit = isCockpitMode;
  
  // Détecter si on est sur la page À propos (masquer le banner)
  const isAboutPage = location.pathname === '/a-propos';
  
  // Détecter si on est sur Espace Pro (masquer le banner)
  const isEspaceProMode = location.pathname.startsWith('/pro');
  
  // Détecter si on est sur la page AbracadaWorld Home (masquer le banner mobile)
  const isAbracadaWorldHome = location.pathname === '/abracadaworld';
  
  // Détecter si on est sur tablette
  const [isTablet, setIsTablet] = useState(false);
  
  useEffect(() => {
    const checkTablet = () => setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    checkTablet();
    window.addEventListener('resize', checkTablet);
    return () => window.removeEventListener('resize', checkTablet);
  }, []);
  
  // Ne pas afficher sur la page À propos uniquement
  // Le bandeau s'affiche maintenant aussi sur les pages Réseau
  if (isAboutPage) {
    return null;
  }
  
  return (
    <>
      {/* ═══ MOBILE (< 768px) — NAVBAR UNIFIÉE STYLE COCKPIT — TOUJOURS VISIBLE ═══ */}
      <>
          <header 
            className="flex md:hidden items-center justify-between border-b bg-white border-gray-100"
            style={{ 
              height: '64px', 
              paddingLeft: '12px', 
              paddingRight: '12px',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1001,
            }}
          >
            {/* Gauche: Menu Univers */}
            <div className="flex items-center gap-2">
              <UniversHamburgerMenu />
            </div>
            
            {/* Droite: Mode Selector, Panier, Messages, Notifications, Profil */}
            <div className="flex items-center gap-2">
              {/* Mode Selector (Shop/Réseau/Pro) */}
              <div className="relative">
                <button
                  onClick={() => setShowModeDropdown(!showModeDropdown)}
                  className="h-9 px-2.5 rounded-full flex items-center gap-1.5"
                  style={{
                    backgroundColor: isProMode
                      ? 'rgba(30, 58, 95, 0.12)'
                      : isShopMode
                        ? 'rgba(249, 115, 22, 0.15)'
                        : 'rgba(59, 130, 246, 0.15)',
                    border: `1px solid ${isProMode ? 'rgba(30, 58, 95, 0.3)' : isShopMode ? 'rgba(249, 115, 22, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
                  }}
                  data-testid="mode-selector-btn"
                >
                  <span
                    className="text-[12px] font-bold"
                    style={{ color: isProMode ? '#1E3A5F' : isShopMode ? '#F97316' : '#3B82F6' }}
                  >
                    {isProMode ? 'Pro' : isShopMode ? 'Shop' : 'Réseau'}
                  </span>
                  <ChevronDown
                    size={14}
                    style={{ color: isProMode ? '#1E3A5F' : isShopMode ? '#F97316' : '#3B82F6' }}
                    className={`transition-transform ${showModeDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                
                {/* Dropdown */}
                <AnimatePresence>
                  {showModeDropdown && (
                    <>
                      {/* Overlay */}
                      <div 
                        className="fixed inset-0 z-[200]" 
                        onClick={() => setShowModeDropdown(false)}
                      />
                      {/* Menu */}
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full right-0 mt-1 w-36 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[201]"
                      >
                        <button
                          onClick={() => {
                            setMode(NAV_MODES.SHOP);
                            navigate('/');
                            setShowModeDropdown(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${isShopMode ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isShopMode ? '#F97316' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                            <polyline points="9 22 9 12 15 12 15 22"/>
                          </svg>
                          <span className={`text-sm font-medium ${isShopMode ? 'text-[#F97316]' : 'text-gray-700'}`}>Shop</span>
                          {isShopMode && <span className="ml-auto text-[#F97316]">✓</span>}
                        </button>
                        <button
                          onClick={() => {
                            setMode(NAV_MODES.RESEAU);
                            navigate('/reseau');
                            setShowModeDropdown(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${isReseauMode ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isReseauMode ? '#3B82F6' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                          </svg>
                          <span className={`text-sm font-medium ${isReseauMode ? 'text-[#3B82F6]' : 'text-gray-700'}`}>Réseau</span>
                          {isReseauMode && <span className="ml-auto text-[#3B82F6]">✓</span>}
                        </button>
                        <div className="border-t border-gray-100" />
                        <button
                          onClick={() => {
                            navigate('/cockpit-pro');
                            setShowModeDropdown(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${isProMode ? 'bg-[#1E3A5F]/5' : 'hover:bg-gray-50'}`}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isProMode ? '#1E3A5F' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                          </svg>
                          <span className={`text-sm font-medium ${isProMode ? 'text-[#1E3A5F]' : 'text-gray-700'}`}>Pro</span>
                          {isProMode && <span className="ml-auto text-[#1E3A5F]">✓</span>}
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Panier */}
              <button
                onClick={() => navigate('/panier')}
                className="relative p-1.5"
                data-testid="cart-btn-public"
              >
                <ShoppingCart size={22} className="text-[#1E3A5F]/70" strokeWidth={1.6} />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center"
                    style={{ fontSize: '9px', fontWeight: '700', color: '#ffffff' }}
                  >
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
              
              {/* Messages - Redirige vers AbracadaTchat */}
              <button 
                onClick={() => navigate('/messages')}
                className="relative p-1.5" 
                data-testid="messages-btn-public"
              >
                <MessageCircle size={22} className="text-[#1E3A5F]/70" strokeWidth={1.6} />
              </button>
              
              {/* Notifications */}
              <button
                onClick={() => navigate('/cockpit-particulier')}
                className="relative p-1.5"
                data-testid="notifications-btn-public"
              >
                <Bell size={22} className="text-[#1E3A5F]/70" strokeWidth={1.6} />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#F97316] rounded-full" />
              </button>
              
              {/* Profil avec photo - remplace la loupe */}
              <button 
                onClick={() => navigate(isAuthenticated ? '/cockpit-particulier' : '/connexion')}
                className="relative w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors" 
                data-testid="profile-btn-public"
              >
                {isAuthenticated && user?.photo_url ? (
                  <img 
                    src={user.photo_url} 
                    alt="Profil" 
                    className="w-full h-full object-cover"
                  />
                ) : isAuthenticated && user?.logo_url ? (
                  <img 
                    src={user.logo_url} 
                    alt="Logo Pro" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={40} className="text-[#1E3A5F]/70" strokeWidth={1.3} />
                )}
              </button>
            </div>
          </header>
          {/* Spacer pour compenser la navbar fixed */}
          <div className="block md:hidden" style={{ height: '64px' }} />

          {/* ═══ BANDEAU BÊTA MOBILE — sous la navbar ═══ */}
          <div
            className="block md:hidden w-full text-center text-white text-xs font-medium py-1.5"
            style={{
              background: 'linear-gradient(90deg, #1E3A5F 0%, #2D5A8F 100%)',
              position: 'fixed',
              top: '64px',
              left: 0,
              right: 0,
              zIndex: 1000,
            }}
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              Bêta — Contenus fictifs | Ouverture 1er juillet 2026
            </span>
          </div>
          {/* Spacer pour compenser le bandeau bêta fixed */}
          <div className="block md:hidden" style={{ height: '30px' }} />
        </>
    </>
  );
}

// Hauteur du bandeau pour les calculs de position
export const TOP_BANNER_HEIGHT = {
  desktop: 0,
  mobile: 96, // Header (64px) + Bandeau bêta (32px)
};
